// The unpaged catalog (AppCatalogRest.catalog, a bare JSON array) — only the advert form's app picker reads it
// now. Guarded anyway: the form calls .filter() during render, and this app has no error boundary, so a
// non-array body would throw inside render rather than showing a failure.
const rows = payload => (Array.isArray(payload) ? payload : []);

// A Spring Page, guarded the same way and for the same reason — the adverts tab, All Apps (both paged since
// UN-3339) and InstalledSites map over `content` and hand the envelope to XMPagination, which reads
// number/totalPages/first/last.
const EMPTY_PAGE = { content: [], number: 0, totalPages: 0, totalElements: 0, first: true, last: true };

/**
 * The page envelope needs the same is-prefix translation as the row booleans (see rowParts.flag): the
 * host's JacksonConfig renames boolean accessors, so Spring's Page reaches the wire as
 * {"isFirst": true, "isLast": false, "isEmpty": false} — verified against the running store-manager.
 * XMPagination reads `first`/`last`, and undefined on both made it render Prev on page 0 and Next on
 * the last page. Translated here, at the one boundary where the wire shape is known.
 */
export const normalizePage = payload => {
  if (!payload || !Array.isArray(payload.content)) return EMPTY_PAGE;
  const first = payload.first ?? payload.isFirst;
  const last = payload.last ?? payload.isLast;
  return {
    ...EMPTY_PAGE,
    ...payload,
    first: first ?? (payload.number || 0) === 0,
    last: last ?? (payload.number || 0) >= (payload.totalPages || 1) - 1
  };
};

// An answer to an installed-sites fetch that a newer one has since replaced (see fetchInstalledSites).
const isStaleSites = (state, action) =>
  action.requestId !== undefined && state.sitesRequestId !== null && action.requestId !== state.sitesRequestId;

const initialState = {
  // the adverts tab's current page
  adverts: EMPTY_PAGE,
  advertsLoaded: false,
  // the whole catalog, for the advert form's app picker
  apps: [],
  appsLoaded: false,
  // the All Apps tab's current page, and its chip counts over the current search (null = unknown)
  appsPage: EMPTY_PAGE,
  appsPageLoaded: false,
  appCounts: null,
  error: null,
  // The detail and installed-sites screens keep their own error slots. Sharing `error` with the list
  // tabs meant a failed save painted a red alert over both lists the next time they mounted.
  advert: null,
  advertLoading: false,
  advertError: null,
  app: null,
  appError: null,
  sites: EMPTY_PAGE,
  sitesLoaded: false,
  sitesError: null,
  // The latest installed-sites fetch; an answer to any earlier one is stale and dropped.
  sitesRequestId: null,
  // The delete has its own slot: a failed delete must not be overwritten by the next list fetch succeeding, and a
  // delete that works on retry must clear the message the failed one left (reviews on #278).
  deleteError: null
};

export default function appStoreReducer(state = initialState, action) {
  switch (action.type) {
    case 'APPSTORE_ADVERTS_PAGE_SUCCESS':
      return { ...state, adverts: normalizePage(action.payload), advertsLoaded: true, error: null };
    case 'APPSTORE_ADVERTS_PAGE_ERROR':
      return { ...state, advertsLoaded: true, error: action.payload };
    case 'APPSTORE_APPS_GETALL_SUCCESS':
      return { ...state, apps: rows(action.payload), appsLoaded: true, error: null };
    case 'APPSTORE_APPS_GETALL_ERROR':
      return { ...state, appsLoaded: true, error: action.payload };
    case 'APPSTORE_APPS_PAGE_SUCCESS':
      return { ...state, appsPage: normalizePage(action.payload), appsPageLoaded: true, error: null };
    case 'APPSTORE_APPS_PAGE_ERROR':
      return { ...state, appsPageLoaded: true, error: action.payload };
    case 'APPSTORE_APP_COUNTS_SUCCESS':
      return { ...state, appCounts: action.payload || null };
    case 'APPSTORE_APP_COUNTS_ERROR':
      return { ...state, appCounts: null };
    case 'APPSTORE_ADVERT_DELETE_SUCCESS':
      // The row leaves the page at once; the tab then re-reads the page, so the next advert moves up into it.
      return {
        ...state,
        adverts: {
          ...state.adverts,
          content: state.adverts.content.filter(a => a.uuid !== action.payload)
        },
        deleteError: null
      };
    case 'APPSTORE_ADVERT_DELETE_ERROR':
      return { ...state, deleteError: action.payload };

    case 'APPSTORE_ADVERT_CLEAR':
      // Opening "New App Advert": the last advert's detail and — above all — the last failed save's
      // error must not carry over; without this a 422 from a publish attempt on one advert sat in
      // the alert of the next blank form.
      return { ...state, advert: null, advertLoading: false, advertError: null };
    case 'APPSTORE_ADVERT_GET_REQUEST':
      // The previous advert is dropped on purpose: keeping it meant opening a second advert showed
      // the first one's fields until the new detail landed.
      return { ...state, advert: null, advertLoading: true, advertError: null };
    case 'APPSTORE_ADVERT_GET_SUCCESS':
      return { ...state, advert: action.payload, advertLoading: false, advertError: null };
    case 'APPSTORE_ADVERT_GET_ERROR':
    case 'APPSTORE_ADVERT_SAVE_ERROR':
      // A failed save keeps `advert` — the form reads the persisted status and audit from it, and
      // blanking it would replace the user's screen with a spinner they cannot retry from.
      return { ...state, advertLoading: false, advertError: action.payload };

    case 'APPSTORE_APP_GET_SUCCESS':
      return { ...state, app: action.payload, appError: null };
    case 'APPSTORE_APP_GET_ERROR':
      // its own slot: getApp serves the installed-sites heading AND the New Advert pre-fill, and a failure
      // of one used to paint over the other screen through sitesError (reviews on #282)
      return { ...state, appError: action.payload };

    case 'APPSTORE_SITES_GET_REQUEST':
      // drop the previous page: opening app B after app A showed A's rows under B's heading until the new
      // page landed, and the loading row only ever appeared on the first fetch of the session
      return {
        ...state,
        sites: EMPTY_PAGE,
        sitesLoaded: false,
        sitesError: null,
        sitesRequestId: action.requestId ?? null
      };
    case 'APPSTORE_SITES_GET_SUCCESS':
      if (isStaleSites(state, action)) return state;
      return { ...state, sites: normalizePage(action.payload), sitesLoaded: true, sitesError: null };
    case 'APPSTORE_SITES_GET_ERROR':
      if (isStaleSites(state, action)) return state;
      return { ...state, sitesLoaded: true, sitesError: action.payload };

    default:
      return state;
  }
}
